import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, ArrowRight, CheckCircle2, Circle, Heart, Users } from "lucide-react";

const RULES = [
  { key: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "One uppercase letter (A–Z)", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter (a–z)", test: (p) => /[a-z]/.test(p) },
  { key: "num", label: "One number (0–9)", test: (p) => /[0-9]/.test(p) },
  { key: "special", label: "One special character (! @ # $ % ^ & *)", test: (p) => /[!@#$%^&*]/.test(p) },
];

const ROLES = [
  { v: "wali", label: "Wali / Parent" },
  { v: "imam", label: "Imam" },
  { v: "registrar", label: "Islamic Marriage Registrar / Officiant" },
];

// NOTE: backend canonical value for the registrar/officiant role is "registrar".

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [joiningAs, setJoiningAs] = useState("member"); // member | assistant
  const [assistantRole, setAssistantRole] = useState("wali");
  const [form, setForm] = useState({ name: "", email: "", password: "", gender: "" });
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const rulesPass = RULES.every((r) => r.test(form.password));
  const matches = form.password.length > 0 && form.password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    if (!form.name.trim()) return setError("Please enter a display name.");
    if (!form.gender) return setError("Please select whether you are a brother or sister.");
    if (!rulesPass) return setError("Your password does not meet all the requirements below.");
    if (!matches) return setError("Passwords do not match.");
    if (!consent) return setError("Please agree to the Terms of Service & Privacy Policy to continue.");
    setLoading(true);
    try {
      const isAssistant = joiningAs === "assistant";
      await register({ ...form, consent, accountType: isAssistant ? "assistant" : "member", assistantRole: isAssistant ? assistantRole : undefined });
      toast.success(isAssistant ? "Account created! Please verify your email to start browsing." : "Account created! Let's set up your profile.");
      navigate(isAssistant ? "/assist/browse" : "/onboarding", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your profile"
      subtitle="Begin your journey toward a blessed marriage."
      footer={<>Already have an account? <Link to="/login" className="font-semibold text-emerald-800 hover:underline" data-testid="go-login-link">Log in</Link></>}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="register-form" autoComplete="off">
        <div className="space-y-2">
          <Label>I'm joining as</Label>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => setJoiningAs("member")} data-testid="register-joining-member"
              className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3.5 text-sm font-semibold transition-colors ${joiningAs === "member" ? "border-emerald-700 bg-emerald-700 text-white" : "border-emerald-900/15 text-emerald-900 hover:border-emerald-400 hover:bg-mint"}`}>
              <Heart className={`h-4 w-4 ${joiningAs === "member" ? "text-amber-300" : "text-emerald-700"}`} /> Looking for marriage
            </button>
            <button type="button" onClick={() => setJoiningAs("assistant")} data-testid="register-joining-assistant"
              className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3.5 text-sm font-semibold transition-colors ${joiningAs === "assistant" ? "border-emerald-700 bg-emerald-700 text-white" : "border-emerald-900/15 text-emerald-900 hover:border-emerald-400 hover:bg-mint"}`}>
              <Users className={`h-4 w-4 ${joiningAs === "assistant" ? "text-amber-300" : "text-emerald-700"}`} /> Helping someone
            </button>
          </div>
        </div>

        {joiningAs === "assistant" && (
          <div className="space-y-2" data-testid="register-role-section">
            <Label>My role</Label>
            <div className="flex flex-wrap gap-2">
              {ROLES.map((r) => (
                <button type="button" key={r.v} onClick={() => setAssistantRole(r.v)} data-testid={`register-role-${r.v}`}
                  className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition-colors ${assistantRole === r.v ? "border-emerald-700 bg-emerald-700 text-white" : "border-emerald-900/15 text-emerald-900 hover:border-emerald-400 hover:bg-mint"}`}>
                  {r.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500">You'll browse eligible profiles to help a son, daughter, family member, or client — you won't appear as a prospective match.</p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="name">Display name</Label>
          <Input id="name" name="nm-field" required value={form.name} onChange={set("name")} data-testid="register-name-input" placeholder="e.g. Aisha" autoComplete="off" />
        </div>

        <div className="space-y-2">
          <Label>I am</Label>
          <div className="grid grid-cols-2 gap-3">
            {[{ v: "male", label: "Brother", sym: "♂" }, { v: "female", label: "Sister", sym: "♀" }].map((g) => (
              <button type="button" key={g.v} onClick={() => setForm((f) => ({ ...f, gender: g.v }))} data-testid={`register-gender-${g.v}`}
                className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3.5 text-base font-semibold transition-colors ${form.gender === g.v ? "border-emerald-700 bg-emerald-700 text-white" : "border-emerald-900/15 text-emerald-900 hover:border-emerald-400 hover:bg-mint"}`}>
                <span className={`text-lg ${form.gender === g.v ? "text-amber-300" : "text-emerald-700"}`}>{g.sym}</span> {g.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" name="email-field" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" placeholder="you@email.com" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input id="password" name="new-password-field" type={show ? "text" : "password"} required value={form.password} onChange={set("password")} data-testid="register-password-input" placeholder="Create a password" autoComplete="new-password" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" aria-label="Toggle password">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          </div>
        </div>

        {(form.password.length > 0 || true) && (
          <div className="rounded-xl border border-emerald-900/10 bg-mint/30 p-4" data-testid="password-rules">
            <div className="space-y-1.5">
              {RULES.map((r) => {
                const ok = r.test(form.password);
                return (
                  <div key={r.key} className="flex items-center gap-2 text-sm" data-testid={`rule-${r.key}`}>
                    {ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="h-4 w-4 shrink-0 text-slate-300" />}
                    <span className={ok ? "text-emerald-800" : "text-slate-500"}>{r.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="confirm">Confirm password</Label>
          <div className="relative">
            <Input id="confirm" name="confirm-password-field" type={showConfirm ? "text" : "password"} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="register-confirm-password-input" placeholder="Re-enter your password" autoComplete="new-password" />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" aria-label="Toggle confirm password">{showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          </div>
          {confirm.length > 0 && !matches && <p className="text-xs text-red-500">Passwords do not match.</p>}
        </div>

        <label className="flex items-start gap-3 rounded-xl bg-emerald-50/60 p-3 text-sm text-slate-600">
          <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="register-consent-checkbox" className="mt-0.5" />
          <span>I agree to the Terms of Service &amp; Privacy Policy, understand members are responsible for their own profile accuracy, that Nikah Match cannot guarantee compatibility or marriage outcomes, and I will use the platform respectfully and honestly.</span>
        </label>

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="register-error">{error}</p>}

        <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full rounded-full bg-emerald-800 py-6 text-white hover:bg-emerald-900">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRight className="mr-2 h-4 w-4" /> Continue</>}
        </Button>
      </form>
    </AuthShell>
  );
}
